import { PropsWithChildren, createContext, memo, useContext, useMemo } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
export const fiveMinsMs = 5 * 60 * 1000;
export const oneMinMs = 1 * 60 * 1000;

/**
 * IMPORTANT:
 *
 * Query clients MUST be stable referentially; they have to be
 * defined outside of react components or useMemo with no dependencies.
 * see: https://tanstack.com/query/v5/docs/eslint/stable-query-client
 *
 * If we need to set some static options we can set it here.
 * Else we can use the queryClient.setDefaultOptions() method via hook.
 *
 * React-admin v5 uses @tanstack/react-query v5, so we no longer need
 * a separate v3 client. All query invalidation/cache operations use
 * the single v5 QueryClient passed to both <AdminContext> and
 * <QueryClientProvider>.
 *
 * Debugger tools:
 * import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
 * <ReactQueryDevtools initialIsOpen={true} />
 */

export type TCosmosQueryClientCtx = {
  queryClient: QueryClient;
};

const CosmosQueryClientContext = createContext<TCosmosQueryClientCtx | undefined>(
  undefined,
);

export const useCosmosQueryClient = () => {
  const context = useContext(CosmosQueryClientContext);
  if (!context) {
    throw new Error(
      'useCosmosQueryClient must be used within a CosmosQueryClientProvider',
    );
  }
  return context;
};

export const CosmosQueryClientProvider = memo(({ children }: PropsWithChildren) => {
  /**
   * Single v5 query-client shared by the whole app (including React-Admin v5).
   * refetchOnMount - True is the RQ Default.
   * - eg: refetch a search view when its re-mounted via back to search.
   */
  const queryClientV5 = useMemo<QueryClient>(() => {
    return new QueryClient({
      defaultOptions: {
        queries: {
          retry: false,
          refetchOnWindowFocus: false,
          refetchOnReconnect: false,
          refetchOnMount: true,
        },
        mutations: {
          retry: false,
        },
      },
    });
  }, []);

  const contextValue = useMemo(() => {
    return {
      queryClient: queryClientV5,
    };
  }, [queryClientV5]);

  return (
    <CosmosQueryClientContext.Provider value={contextValue}>
      <QueryClientProvider client={queryClientV5}>{children}</QueryClientProvider>
    </CosmosQueryClientContext.Provider>
  );
});
CosmosQueryClientProvider.displayName = 'CosmosQueryClientProvider';

/**
 * Notes:
 *
 * Ensure upon site switching in CosmosContext that the queryClient
 * is reset and all query and cache is cleared. As this is to clear the cached
 * results as they correspond to the activeSite (x-blox-domain) of the fetch.
 *
 *
 * CMSADMIN-1045
 * Maybe something like this inside of a useEffect within CosmosContext, perhaps
 * once the initial getSessionConfigBasic is successful so we have the activeSite set...
 *
 * note it will affect all queries within the app and we should be careful so we may
 * need to have a way to exclude certain string like an 'exclude' or 'skipKey', some flag
 * that we can check for in the queryKey hash fn indicating to not add the authQueryKeys.
 *
 * May have to manually add some of these to the initCosmosQuery useQuery
 * like `site:${usingDomain}`,but not isAuthed as or userId, and maybe keepPreviousData: true...
 * as we would not want the key to change causing a refetch or unmount of child components...
 *
 *
 * Can confirm though that this method would allow us to add the authQueryKeys to the queryKey
 * of React-Admin components, as they currently seem to ignore the queryKey prop...
 *
 * // e.g. ... something like this
 *  const auth = useAuth();
 *  const { activeSite } = useCosmos();
 *  const authQueryKeys = getAuthQueryKeysArr({
 *    site: activeSite,
 *   .. and maybe some others?
 *  });
 *
 * const setDefaultOptions = async () => {
 *    queryClient.setDefaultOptions({
 *      queries: {
 *        queryKeyHashFn: (queryKey) => {
 *          // copy any existing queryKey, normalize to array
 *          const copyKeys = Array.isArray(queryKey) ? [...queryKey] : [queryKey];
 *          // add the authQueryKeys to the queryKey
 *          const updatedKeys = [...authQueryKeys, ...copyKeys];
 *          // hash the new queryKey with rq's hash fn
 *          const hashedNewKeys = hashQueryKey(updatedKeys);
 *          // Return the new queryKey
 *          return hashedNewKeys;
 *        },
 *      },
 *    });
 *  };
 *
 *
 */
